import { Box, Button, CircularProgress } from '@mui/material';
import { format, isToday } from 'date-fns';
import { useLayoutEffect, useRef } from 'react';

import type { CalendarEvent, ISODate, Member } from '../types';
import { dateCompare, formatTime, parseISODate, tint } from '../utils';

const SCROLL_THRESHOLD_PX = 200;

const BLOCK_HATCH =
  'repeating-linear-gradient(45deg, #f0ede8, #f0ede8 2px, #e0ddd8 2px, #e0ddd8 4px)';
const OVERRIDE_HATCH =
  'repeating-linear-gradient(45deg, #fce8c8, #fce8c8 2px, #f5d89c 2px, #f5d89c 4px)';

type ScheduleListViewProps = {
  cursor: ISODate;
  events: CalendarEvent[];
  members?: Member[];
  selectedEventId?: string | null;
  selectedMemberIds?: string[] | null;
  onEventClick: (event: CalendarEvent) => void;
  /** Called when scrolled near the bottom — load the next page of events. */
  onReachEnd?: () => void;
  /** Called when scrolled near the top — load the previous page of events. */
  onReachStart?: () => void;
  isLoadingInitial?: boolean;
  isLoadingFuture?: boolean;
  isLoadingPast?: boolean;
  pageError?: 'past' | 'future' | null;
};

type DayGroup = {
  date: ISODate;
  events: CalendarEvent[];
};

function buildDayGroups(
  events: CalendarEvent[],
  selectedMemberIds?: string[] | null,
): DayGroup[] {
  // Group every loaded event by day; the parent controls which range is loaded.
  const dateSet = new Set<ISODate>();
  for (const ev of events) {
    if (ev.kind === 'session' && selectedMemberIds && ev.memberId) {
      if (!selectedMemberIds.includes(ev.memberId)) continue;
    }
    dateSet.add(ev.date);
  }

  const dates = Array.from(dateSet).sort(dateCompare);

  return dates.map((date) => ({
    date,
    events: events
      .filter((ev) => {
        if (ev.date !== date) return false;
        if (ev.kind === 'session' && selectedMemberIds && ev.memberId) {
          return selectedMemberIds.includes(ev.memberId);
        }
        return true;
      })
      .sort((a, b) => a.start - b.start),
  }));
}

function EventDot({ event, member }: { event: CalendarEvent; member?: Member }) {
  const isSession = event.kind === 'session';
  const isBlock = event.kind === 'block';

  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: '3px',
        flexShrink: 0,
        ...(isSession && {
          background: member ? tint(member.color) : '#d8e2cc',
          border: `1px solid ${member?.color ?? '#5e8f3e'}`,
        }),
        ...(isBlock && { background: BLOCK_HATCH }),
        ...(!isSession && !isBlock && { background: OVERRIDE_HATCH }),
      }}
    />
  );
}

export function ScheduleListView({
  cursor,
  events,
  members = [],
  selectedEventId,
  selectedMemberIds,
  onEventClick,
  onReachEnd,
  onReachStart,
  isLoadingInitial,
  isLoadingFuture,
  isLoadingPast,
  pageError,
}: ScheduleListViewProps) {
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const groups = buildDayGroups(events, selectedMemberIds);

  // Anchor = the cursor's day (or the next day with events). The list scrolls
  // here on load and whenever the cursor changes (e.g. the Today button).
  const anchorDate = groups.find((g) => dateCompare(g.date, cursor) >= 0)?.date;
  const firstGroupDate = groups[0]?.date ?? null;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const scrolledForCursorRef = useRef<ISODate | null>(null);
  // Track the top day + height each render to keep the viewport stable when
  // older events are prepended. A prepend is detected by the first day moving
  // earlier, which is robust to failed or empty loads (no stale delta).
  const prevFirstDateRef = useRef<ISODate | null>(null);
  const prevHeightRef = useRef(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      // Empty/loading (e.g. mid-reset) — drop the baseline so the next load
      // isn't mistaken for a prepend, and allow the scroll-to-cursor to re-run
      // even when the cursor is unchanged (e.g. switching coaches).
      prevFirstDateRef.current = null;
      scrolledForCursorRef.current = null;
      return;
    }
    const prevFirst = prevFirstDateRef.current;
    if (prevFirst && firstGroupDate && dateCompare(firstGroupDate, prevFirst) < 0) {
      container.scrollTop += container.scrollHeight - prevHeightRef.current;
    }
    prevFirstDateRef.current = firstGroupDate;
    prevHeightRef.current = container.scrollHeight;
  });

  // Scroll to the cursor's day on load and whenever the cursor changes (Today
  // button, prev/next). Runs after the prepend adjustment so it wins on reset.
  useLayoutEffect(() => {
    if (scrolledForCursorRef.current === cursor) return;
    const container = containerRef.current;
    // Wait until events have loaded (firstGroupDate is set).
    if (!container || !firstGroupDate) return;
    if (anchorDate && anchorRef.current) {
      container.scrollTop =
        anchorRef.current.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
    } else {
      // No events on/after the cursor (all loaded events are in the past) —
      // land at the bottom, the day nearest to the cursor.
      container.scrollTop = container.scrollHeight;
    }
    scrolledForCursorRef.current = cursor;
  }, [cursor, anchorDate, firstGroupDate]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    if (onReachEnd && el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX) {
      onReachEnd();
    }
    if (onReachStart && !isLoadingPast && el.scrollTop < SCROLL_THRESHOLD_PX) {
      onReachStart();
    }
  };

  if (isLoadingInitial) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          color: '#aaa',
          fontSize: 13,
          background: '#fff',
        }}
      >
        <span>No events in this window.</span>
        {(onReachStart || onReachEnd) && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onReachStart && (
              <Button size="small" disabled={isLoadingPast} onClick={onReachStart}>
                {isLoadingPast ? 'Loading…' : 'Load earlier'}
              </Button>
            )}
            {onReachEnd && (
              <Button size="small" disabled={isLoadingFuture} onClick={onReachEnd}>
                {isLoadingFuture ? 'Loading…' : 'Load later'}
              </Button>
            )}
          </Box>
        )}
        {pageError && (
          <span style={{ color: '#bc4636' }}>Couldn’t load events. Try again.</span>
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{ flex: 1, overflowY: 'auto', background: '#fff' }}
    >
      {isLoadingPast ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : pageError === 'past' && onReachStart ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 1.5,
            fontSize: 12,
            color: '#888',
          }}
        >
          Couldn’t load earlier events.
          <Button size="small" onClick={onReachStart}>
            Retry
          </Button>
        </Box>
      ) : onReachStart ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Button size="small" onClick={onReachStart}>
            Load earlier
          </Button>
        </Box>
      ) : null}
      {groups.map(({ date, events: dayEvents }) => {
        const d = parseISODate(date);
        const todayFlag = isToday(d);

        return (
          <Box
            key={date}
            ref={date === anchorDate ? anchorRef : undefined}
            sx={{ borderBottom: '1px solid #f0ede8' }}
          >
            {/* Sticky day header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr',
                padding: '14px 22px 10px',
                alignItems: 'center',
                background: '#fafaf8',
                borderBottom: '1px solid #f0ede8',
                position: 'sticky',
                top: 0,
                zIndex: 5,
              }}
            >
              {/* Big date number */}
              <Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: 22,
                    fontWeight: 300,
                    color: '#222',
                    lineHeight: 1,
                    ...(todayFlag && {
                      background: '#2a3a22',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      fontWeight: 500,
                    }),
                  }}
                >
                  {format(d, 'd')}
                </Box>
              </Box>

              {/* Day name + month */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Box
                  sx={{
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#888',
                    fontWeight: 500,
                  }}
                >
                  {format(d, 'EEE')}
                </Box>
                <Box sx={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
                  {format(d, 'MMMM yyyy')}
                </Box>
              </Box>
            </Box>

            {/* Event rows */}
            {dayEvents.map((ev) => {
              const member = ev.memberId ? memberMap.get(ev.memberId) : undefined;
              const isSession = ev.kind === 'session';
              const isSelected = ev.id === selectedEventId;
              const title = ev.title ?? member?.name ?? ev.kind;

              const sessionType = ev.meta?.sessionType as string | undefined;
              const duration = ev.meta?.duration as number | undefined;

              return (
                <Box
                  key={ev.id}
                  onClick={() => isSession && onEventClick(ev)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '80px 24px 1fr auto',
                    gap: '12px',
                    padding: '12px 22px',
                    borderBottom: '1px solid #f5f5f3',
                    alignItems: 'center',
                    cursor: isSession ? 'pointer' : 'default',
                    background: isSelected ? '#f5f9f0' : '#fff',
                    outline: isSelected ? '2px solid #2a3a22' : 'none',
                    outlineOffset: -2,
                    '&:hover': isSession ? { background: '#fafaf8' } : {},
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  {/* Time range */}
                  <Box
                    sx={{
                      fontSize: 12,
                      color: '#555',
                      fontWeight: 500,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatTime(ev.start)}
                    {ev.end !== ev.start && (
                      <Box component="span" sx={{ color: '#aaa' }}>
                        {' – '}
                        {formatTime(ev.end)}
                      </Box>
                    )}
                  </Box>

                  {/* Colour dot */}
                  <EventDot event={ev} member={member} />

                  {/* Title + meta */}
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: 13,
                        fontWeight: isSession ? 500 : 400,
                        color: isSession ? '#222' : '#888',
                        fontStyle: isSession ? 'normal' : 'italic',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {title}
                    </Box>
                    {(sessionType || duration) && (
                      <Box sx={{ fontSize: 11, color: '#888', mt: '1px' }}>
                        {[sessionType, duration ? `${duration * 60} min` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </Box>
                    )}
                  </Box>

                  {/* Empty right column — for future badges */}
                  <Box />
                </Box>
              );
            })}
          </Box>
        );
      })}
      {isLoadingFuture ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : pageError === 'future' && onReachEnd ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 1.5,
            fontSize: 12,
            color: '#888',
          }}
        >
          Couldn’t load more events.
          <Button size="small" onClick={onReachEnd}>
            Retry
          </Button>
        </Box>
      ) : onReachEnd ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Button size="small" onClick={onReachEnd}>
            Load later
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
